import { useEffect, useState, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Users, Calendar, User as UserIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const COMP_META = {
  campionato: { label: "Campionato", emoji: "🏆" },
  coppa_di_lega: { label: "Coppa di Lega", emoji: "🥇" },
  champions_league: { label: "Champions League", emoji: "🌟" },
  supercoppa: { label: "Supercoppa", emoji: "👑" },
};
const SEASONS = ["23/24", "24/25", "25/26", "26/27"];

// Map Mantra role token → canonical P/D/C/A group (order matters)
const ROLE_GROUP_ORDER = ["P", "D", "C", "A"];
const ROLE_GROUP_META = {
  P: { label: "Portieri", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  D: { label: "Difensori", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  C: { label: "Centrocampisti", color: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/30" },
  A: { label: "Attaccanti", color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30" },
};

function roleGroupOf(mantraRole) {
  if (!mantraRole) return "?";
  const r = String(mantraRole).trim().toUpperCase();
  if (r.startsWith("POR") || r === "P") return "P";
  // Check first meaningful token/letter
  const tokens = r.split(/[\s,;/]+/);
  for (const t of tokens) {
    if (t.startsWith("POR")) return "P";
    // Difensori: Dc, Ds, Dd, B (braccetto)
    if (t.startsWith("D") || t === "B") return "D";
    // Centrocampisti: E (esterno), M (mediano), C, W (wing)
    if (t === "E" || t.startsWith("M") || t === "C" || t === "W") return "C";
    // Attaccanti: A (ala), Pc (punta centrale), T (trequartista)
    if (t.startsWith("A") || t === "PC" || t === "T") return "A";
  }
  // Fallback based on first char
  const c = r[0];
  if (c === "P") return r.startsWith("PC") ? "A" : "P";
  if (c === "D" || c === "B") return "D";
  if (c === "E" || c === "M" || c === "C" || c === "W") return "C";
  if (c === "A" || c === "T") return "A";
  return "?";
}

export default function StoricoClub() {
  const { clubId } = useParams();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState("26/27");
  const [rooster, setRooster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/magnati/clubs/${clubId}`);
        setClub(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [clubId]);

  useEffect(() => {
    if (!clubId) return;
    setRosterLoading(true);
    api.get(`/magnati/clubs/${clubId}/rooster?season=${encodeURIComponent(season)}`)
      .then(r => setRooster(r.data.players || []))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setRosterLoading(false));
  }, [clubId, season]);

  // Group and sort by canonical role (P → D → C → A), then by final_value desc within group
  const roosterByRole = (() => {
    const groups = { P: [], D: [], C: [], A: [], "?": [] };
    for (const p of rooster) {
      const g = roleGroupOf(p.ruolo);
      (groups[g] || groups["?"]).push(p);
    }
    for (const g of Object.keys(groups)) {
      groups[g].sort((a, b) => (Number(b.final_value) || 0) - (Number(a.final_value) || 0));
    }
    return groups;
  })();

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500">Caricamento…</div>;
  if (!club) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">Club non trovato</div>;

  const totalTrophies = Object.values(club.palmares || {}).reduce((s, x) => s + (x?.length || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <header className="border-b border-amber-500/20 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/storico-magnati" data-testid="club-back-btn"
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-amber-500/70">Club Permanente</div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500">
                {club.current_name}
              </h1>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase tracking-widest font-mono">Manager</div>
            <div className="text-sm font-semibold text-slate-200 flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-amber-400" /> {club.manager_name}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-amber-500/20 to-slate-900 border-amber-500/30">
            <CardContent className="p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-amber-400/80">Trofei Totali</div>
              <div className="text-3xl font-bold text-amber-300 mt-1">{totalTrophies}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Fondato</div>
              <div className="text-3xl font-bold text-slate-200 mt-1">{club.founded_season || "—"}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Rinominazioni</div>
              <div className="text-3xl font-bold text-slate-200 mt-1">{(club.name_history || []).length}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Giocatori all-time</div>
              <div className="text-3xl font-bold text-slate-200 mt-1">{club.total_players_all_time || 0}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="palmares" className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="palmares" data-testid="tab-palmares">🏆 Palmarès</TabsTrigger>
            <TabsTrigger value="storia" data-testid="tab-storia">📜 Storia</TabsTrigger>
            <TabsTrigger value="rosa" data-testid="tab-rosa">👥 Rosa</TabsTrigger>
          </TabsList>

          <TabsContent value="palmares" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {Object.entries(COMP_META).map(([key, meta]) => {
                const wins = club.palmares?.[key] || [];
                return (
                  <Card key={key} className={`bg-slate-900/60 border-slate-800 ${wins.length > 0 ? "border-amber-500/30" : ""}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{meta.emoji}</span>
                          <span className="font-semibold text-slate-200">{meta.label}</span>
                        </div>
                        <Badge className={wins.length ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-500"}>
                          {wins.length}
                        </Badge>
                      </div>
                      {wins.length === 0 ? (
                        <div className="text-sm text-slate-600 italic">Nessuna vittoria</div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {wins.map(s => (
                            <span key={s} className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-mono border border-amber-500/40">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="storia">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-400"/>Timeline Nomi</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative pl-8 border-l-2 border-amber-500/30 space-y-4">
                  {(club.name_history || []).map((h, i) => (
                    <div key={i} className="relative">
                      <div className="absolute -left-[35px] top-2 w-4 h-4 rounded-full bg-amber-500 border-4 border-slate-950" />
                      <div className="text-xs font-mono text-amber-400/80">Stagione {h.season}</div>
                      <div className="text-lg font-bold text-slate-100">{h.name}</div>
                    </div>
                  ))}
                </div>
                {(club.past_managers || []).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-800">
                    <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Ex Manager</div>
                    {(club.past_managers || []).map((pm, i) => (
                      <div key={i} className="text-sm text-slate-300">
                        <span className="text-amber-400 font-semibold">{pm.name}</span>
                        <span className="text-slate-500 ml-2">({(pm.seasons || []).join(", ")})</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rosa">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-amber-400"/>Rosa</CardTitle>
                  <div className="flex gap-2">
                    {SEASONS.map(s => (
                      <button key={s}
                        onClick={() => setSeason(s)}
                        data-testid={`rosa-season-${s.replace("/","")}`}
                        className={`px-3 py-1 rounded-md text-xs font-mono transition-colors ${
                          season === s ? "bg-amber-500 text-slate-950 font-bold" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                        }`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {rosterLoading ? (
                  <div className="text-slate-500 text-sm text-center py-6">Caricamento rosa…</div>
                ) : rooster.length === 0 ? (
                  <div className="text-slate-600 italic text-center py-6">Nessun giocatore per la stagione {season}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-slate-400">Ruolo</TableHead>
                          <TableHead className="text-slate-400">Giocatore</TableHead>
                          <TableHead className="text-slate-400">Squadra A</TableHead>
                          <TableHead className="text-right text-slate-400">Costo</TableHead>
                          <TableHead className="text-right text-slate-400">Valore</TableHead>
                          <TableHead className="text-right text-slate-400">Stipendio</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ROLE_GROUP_ORDER.concat(roosterByRole["?"]?.length ? ["?"] : []).map(gKey => {
                          const list = roosterByRole[gKey] || [];
                          if (list.length === 0) return null;
                          const meta = ROLE_GROUP_META[gKey] || { label: "Altri", color: "text-slate-400", bg: "bg-slate-800/40", border: "border-slate-700" };
                          return (
                            <Fragment key={`group-${gKey}`}>
                              <TableRow className={`${meta.bg} border-t border-b ${meta.border}`} data-testid={`role-group-${gKey}`}>
                                <TableCell colSpan={6} className={`text-[10px] font-mono uppercase tracking-widest font-bold ${meta.color} py-1.5`}>
                                  {meta.label} · {list.length}
                                </TableCell>
                              </TableRow>
                              {list.map((p, i) => (
                                <TableRow key={`${p.player_id}-${gKey}-${i}`} className="hover:bg-slate-800/40">
                                  <TableCell><span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400 font-mono">{p.ruolo || "—"}</span></TableCell>
                                  <TableCell>
                                    <Link to={`/storico-magnati/giocatore/${p.player_id}`} className="text-slate-100 hover:text-amber-400 font-medium">
                                      {p.canonical_name}
                                    </Link>
                                  </TableCell>
                                  <TableCell className="text-slate-400 uppercase text-xs font-mono">{p.squadra_a || "—"}</TableCell>
                                  <TableCell className="text-right font-mono text-slate-300">{p.acquisition_cost ?? "—"}</TableCell>
                                  <TableCell className="text-right font-mono text-amber-300">{p.final_value ?? "—"}</TableCell>
                                  <TableCell className="text-right font-mono text-slate-400">{p.salary ?? "—"}</TableCell>
                                </TableRow>
                              ))}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <div className="text-xs text-slate-600 mt-3 text-right">
                      {rooster.length} giocatori · Valore totale: <span className="text-amber-300 font-mono font-bold">
                        {rooster.reduce((s, p) => s + (Number(p.final_value) || 0), 0).toFixed(0)}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
